import { useState } from 'react';
import {
  ASSIGNEES,
  CATEGORIES,
  CATEGORY_META,
  PRIORITIES,
  type Assignee,
  type Category,
  type Idea,
  type Priority,
} from '@travel-planner/shared';
import { useTripContext } from '../trips/TripContext';
import { assigneeLabel } from '../lists/itemUi';
import { useCreateIdea, useDeleteIdea, useIdeas, usePromoteIdea } from './useIdeas';

export function IdeaInbox({ tripId }: { tripId: string }) {
  const { data: ideas = [] } = useIdeas(tripId);
  const [open, setOpen] = useState(false);
  const pending = ideas.filter((i) => !i.promotedToItemId);

  return (
    <section className="card mt-4">
      <div className="card-body p-3">
        <button
          className="btn btn-ghost btn-sm fw-semibold px-2"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? '▾' : '▸'} Idea inbox{' '}
          <span className="text-body-tertiary fw-normal">({pending.length})</span>
        </button>
        {open && (
          <div className="mt-2">
            <AddIdea tripId={tripId} />
            <div className="d-flex flex-column gap-2 mt-3">
              {ideas.length === 0 && (
                <p className="text-body-tertiary small mb-0">
                  Dump anything here — promote the good ones into a country list later.
                </p>
              )}
              {ideas.map((idea) => (
                <IdeaRow key={idea.id} idea={idea} tripId={tripId} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function AddIdea({ tripId }: { tripId: string }) {
  const { countries } = useTripContext();
  const create = useCreateIdea(tripId);
  const [body, setBody] = useState('');
  const [link, setLink] = useState('');
  const [countryId, setCountryId] = useState('');

  return (
    <form
      className="d-flex flex-column gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!body.trim()) return;
        create.mutate(
          { body: body.trim(), link: link.trim() || undefined, countryId: countryId || null },
          {
            onSuccess: () => {
              setBody('');
              setLink('');
            },
          },
        );
      }}
    >
      <textarea
        className="form-control"
        placeholder="A rough idea…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={2}
        style={{ resize: 'vertical' }}
      />
      <div className="d-flex gap-2 flex-wrap">
        <input
          className="form-control form-control-sm"
          style={{ flex: 1, minWidth: 160 }}
          placeholder="https://link (optional)"
          value={link}
          onChange={(e) => setLink(e.target.value)}
        />
        <select
          className="form-select form-select-sm"
          style={{ width: 'auto' }}
          value={countryId}
          onChange={(e) => setCountryId(e.target.value)}
        >
          <option value="">No country</option>
          {countries.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <button type="submit" className="btn btn-primary btn-sm" disabled={!body.trim() || create.isPending}>
          {create.isPending ? 'Adding…' : 'Add idea'}
        </button>
      </div>
    </form>
  );
}

function IdeaRow({ idea, tripId }: { idea: Idea; tripId: string }) {
  const del = useDeleteIdea(tripId);
  const [promoting, setPromoting] = useState(false);
  const promoted = Boolean(idea.promotedToItemId);

  return (
    <div
      className="rounded-3 p-2"
      style={{
        border: '1px solid var(--border)',
        background: promoted ? 'var(--surface-2)' : 'var(--surface)',
        opacity: promoted ? 0.7 : 1,
      }}
    >
      <div className="d-flex gap-2 align-items-start">
        <div className="flex-grow-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {idea.body}
          {idea.link && (
            <>
              {' '}
              <a href={idea.link} target="_blank" rel="noreferrer" className="small">
                link ↗
              </a>
            </>
          )}
          {promoted && <span className="ms-2 text-success" style={{ fontSize: '0.72rem' }}>→ promoted</span>}
        </div>
        {!promoted && (
          <div className="d-flex gap-1">
            <button className="btn btn-ghost btn-sm" onClick={() => setPromoting((v) => !v)}>
              {promoting ? 'Cancel' : 'Promote'}
            </button>
            <button className="btn btn-ghost btn-sm text-danger" onClick={() => del.mutate(idea.id)}>
              ✕
            </button>
          </div>
        )}
      </div>
      {promoting && <PromoteForm idea={idea} tripId={tripId} onDone={() => setPromoting(false)} />}
    </div>
  );
}

function PromoteForm({ idea, tripId, onDone }: { idea: Idea; tripId: string; onDone: () => void }) {
  const { countries, selectedCountry } = useTripContext();
  const promote = usePromoteIdea(tripId);
  const [countryId, setCountryId] = useState(idea.countryId ?? selectedCountry?.id ?? countries[0]?.id ?? '');
  const [priority, setPriority] = useState<Priority>('should');
  const [category, setCategory] = useState<Category>('sight');
  const [assignee, setAssignee] = useState<Assignee>('both');

  return (
    <form
      className="d-flex gap-2 flex-wrap mt-2 pt-2"
      style={{ borderTop: '1px dashed var(--border)' }}
      onSubmit={(e) => {
        e.preventDefault();
        if (!countryId) return;
        promote.mutate({ ideaId: idea.id, input: { countryId, priority, category, assignee } }, { onSuccess: onDone });
      }}
    >
      <select className="form-select form-select-sm" style={{ width: 'auto' }} value={countryId} onChange={(e) => setCountryId(e.target.value)}>
        {countries.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <select className="form-select form-select-sm" style={{ width: 'auto' }} value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
        {PRIORITIES.map((p) => (
          <option key={p} value={p}>
            {p[0]?.toUpperCase()}
            {p.slice(1)}
          </option>
        ))}
      </select>
      <select className="form-select form-select-sm" style={{ width: 'auto' }} value={category} onChange={(e) => setCategory(e.target.value as Category)}>
        {CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {CATEGORY_META[c].icon} {CATEGORY_META[c].label}
          </option>
        ))}
      </select>
      <select className="form-select form-select-sm" style={{ width: 'auto' }} value={assignee} onChange={(e) => setAssignee(e.target.value as Assignee)}>
        {ASSIGNEES.map((a) => (
          <option key={a} value={a}>
            {assigneeLabel(a)}
          </option>
        ))}
      </select>
      <button type="submit" className="btn btn-primary btn-sm" disabled={!countryId || promote.isPending}>
        {promote.isPending ? 'Promoting…' : 'Add to list'}
      </button>
    </form>
  );
}
